import { type FC, type ReactNode } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import type { AudienceProfile } from '@/types';

// ---------------------------------------------------------------------------
// Audience profile selector — presets visible layers per use-case
// ---------------------------------------------------------------------------

// Minimal SVG icons (14×14 viewport, currentColor stroke)
const ICONS: Record<AudienceProfile, ReactNode> = {
  INTEL: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
      <circle cx="7" cy="7" r="5.5" />
      <circle cx="7" cy="7" r="2" />
      <line x1="7" y1="1.5" x2="7" y2="3.5" />
      <line x1="7" y1="10.5" x2="7" y2="12.5" />
      <line x1="1.5" y1="7" x2="3.5" y2="7" />
      <line x1="10.5" y1="7" x2="12.5" y2="7" />
    </svg>
  ),
  MEDIA: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
      <path d="M2.5 10.5 C2.5 10.5 4 5 7 5 C10 5 11.5 10.5 11.5 10.5" />
      <path d="M4.5 8.5 C4.5 8.5 5.2 6.5 7 6.5 C8.8 6.5 9.5 8.5 9.5 8.5" />
      <circle cx="7" cy="10" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  ),
  FINANCE: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
      <polyline points="1.5,10.5 4.5,6.5 7,8.5 10,4.5 12.5,4.5" />
      <polyline points="10,4.5 12.5,4.5 12.5,7" />
    </svg>
  ),
  INFRA: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
      <rect x="2" y="1.5" width="10" height="4" rx="0.8" />
      <rect x="2" y="6.5" width="10" height="4" rx="0.8" />
      <line x1="5" y1="3.5" x2="5" y2="3.5" strokeWidth="2" strokeLinecap="round" />
      <line x1="7" y1="3.5" x2="7" y2="3.5" strokeWidth="2" strokeLinecap="round" />
      <line x1="5" y1="8.5" x2="5" y2="8.5" strokeWidth="2" strokeLinecap="round" />
      <line x1="7" y1="8.5" x2="7" y2="8.5" strokeWidth="2" strokeLinecap="round" />
      <line x1="7" y1="10.5" x2="7" y2="12.5" />
      <line x1="5" y1="12.5" x2="9" y2="12.5" />
    </svg>
  ),
  PUBLIC: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 1.5 C5.5 3.5 4.5 5.2 4.5 7 C4.5 8.8 5.5 10.5 7 12.5" />
      <path d="M7 1.5 C8.5 3.5 9.5 5.2 9.5 7 C9.5 8.8 8.5 10.5 7 12.5" />
      <line x1="1.5" y1="7" x2="12.5" y2="7" />
      <line x1="2.2" y1="4.5" x2="11.8" y2="4.5" />
      <line x1="2.2" y1="9.5" x2="11.8" y2="9.5" />
    </svg>
  ),
};

const PROFILES: Array<{
  id: AudienceProfile;
  label: string;
  description: string;
  color: string;
}> = [
  {
    id: 'INTEL',
    label: 'INTEL',
    description: 'Full intelligence view — all military, nuclear, missile, and movement layers',
    color: 'border-red-800 text-red-300 hover:bg-red-950/50',
  },
  {
    id: 'MEDIA',
    label: 'MEDIA',
    description: 'Journalist view — conflict zones, news, and route monitoring',
    color: 'border-amber-800 text-amber-300 hover:bg-amber-950/50',
  },
  {
    id: 'FINANCE',
    label: 'FINANCE',
    description: 'Investor view — shipping routes, infrastructure, and commodity risk',
    color: 'border-emerald-800 text-emerald-300 hover:bg-emerald-950/50',
  },
  {
    id: 'INFRA',
    label: 'INFRA',
    description: 'Infrastructure manager view — power plants, cables, and shipping',
    color: 'border-sky-800 text-sky-300 hover:bg-sky-950/50',
  },
  {
    id: 'PUBLIC',
    label: 'PUBLIC',
    description: 'General public view — major events, natural disasters, conflict zones',
    color: 'border-violet-800 text-violet-300 hover:bg-violet-950/50',
  },
];

export const AudienceSelector: FC = () => {
  const activeProfile = useAppStore((s) => s.activeProfile);
  const setProfile = useAppStore((s) => s.setProfile);

  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px] uppercase tracking-wider text-slate-600 mr-0.5 hidden lg:inline">Profile</span>
      {PROFILES.map((p) => (
        <button
          key={p.id}
          title={p.description}
          onClick={() => setProfile(p.id)}
          className={clsx(
            'flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold tracking-wide transition-all',
            p.color,
            activeProfile === p.id
              ? 'opacity-100 ring-1 ring-current'
              : 'opacity-50 border-transparent hover:opacity-80',
          )}
        >
          {ICONS[p.id]}
          <span className="hidden sm:inline">{p.label}</span>
        </button>
      ))}
    </div>
  );
};
